import type {
  ModelCost,
  ModelRoleName,
  ModelState,
} from "@/types/modelRegistry";

const compact = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const usd = new Intl.NumberFormat("en", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export const formatTokens = (tokens: number | null): string =>
  tokens === null ? "Unknown" : compact.format(tokens);

export const isChatCost = (
  cost: ModelCost | null,
): cost is Extract<ModelCost, { input: number }> => !!cost && "input" in cost;

/** "$3 / $15" per million tokens, or the cheapest image price. */
export function formatCost(cost: ModelCost | null): string {
  if (!cost) return "Unknown";
  if (isChatCost(cost))
    return `${usd.format(cost.input)} / ${usd.format(cost.output)}`;
  const cheapest = Math.min(...cost.perImage.map((p) => p.usd));
  return Number.isFinite(cheapest)
    ? `${usd.format(cheapest)} per image`
    : "Unknown";
}

export function formatRelative(iso: string | null): string {
  if (!iso) return "Never";
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

export const ROLE_LABELS: Record<
  ModelRoleName,
  { label: string; hint: string }
> = {
  chat_default: {
    label: "Default chat",
    hint: 'What "Auto" means in the chat picker.',
  },
  title: { label: "Chat titles", hint: "Names a chat from its first turn." },
  compaction: {
    label: "Compaction",
    hint: "Summarizes history when the context fills up.",
  },
};

export const STATE_LABELS: Record<
  ModelState,
  { label: string; hint: string; dot: string }
> = {
  active: {
    label: "Active",
    hint: "Usable and shown in the picker",
    dot: "bg-emerald-500",
  },
  hidden: {
    label: "Hidden",
    hint: "Usable by roles, not shown in the picker",
    dot: "bg-gray-400",
  },
  disabled: {
    label: "Disabled",
    hint: "Not usable anywhere",
    dot: "bg-red-400",
  },
};
